import React, { useState, useEffect, useRef } from "react";
import { ImageSelector } from "./ImageSelector.jsx";
import { showToast } from "components/shared/Toast";

const monthsList = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString("default", { month: "long" })
);

const toSegmentValue = (value = "") => String(value).toLowerCase().trim();
const formatSegmentLabel = (value = "") =>
    toSegmentValue(value)
        .split(/[\s-]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const Overview = ({ location, onSubmit, parentRange, contentType = "travel" }) => {
    const segmentCategory = toSegmentValue(contentType);
    const [selectedImage, setSelectedImage] = useState(location.bannerImg || null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [formData, setFormData] = useState({
        locationName: location.locationName || "",
        category: segmentCategory,
        type: location.type || "",
        parentRange: location.parentRange || "",
        subRange: location.subRange || "",
        Hashtags: location.Hashtags || "",
        address: {
            country: location.address?.country || "",
            state: location.address?.state || "",
            district: location.address?.district || "",
            region: location.address?.region || "",
            altitude: location.address?.altitude || "",
        },
        description: location.description || "",
        monthlyVisitors: location.monthlyVisitors || "",
        bannerImg: location.bannerImg || null,
        bestMonthToVisit: location.bestMonthToVisit || []
    });

    useEffect(() => {
        setFormData((prev) => ({ ...prev, category: segmentCategory }));
    }, [segmentCategory]);

    useEffect(() => {
        if (selectedImage) {
            setFormData((prevData) => ({
                ...prevData,
                bannerImg: {
                    _id: selectedImage._id,
                    title: selectedImage.title,
                    filePath: selectedImage.compressedFilePath || selectedImage.filePath,
                    name: selectedImage.photographerName || selectedImage.name,
                    socialMedia: selectedImage.photographerSocialLinks || selectedImage.socialMedia,
                },
            }));
        } else if (location.bannerImg) {
            // ✅ Retain existing banner image if not updated
            setFormData((prevData) => ({
                ...prevData,
                bannerImg: location.bannerImg,
            }));
        }
    }, [selectedImage, location.bannerImg]);


    const handleChange = (e) => {
        const { name, value } = e.target;

        if (["country", "state", "district", "region", "altitude"].includes(name)) {
            setFormData((prevData) => ({
                ...prevData,
                address: {
                    ...prevData.address,
                    [name]: value,
                },
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
        }
    };
    const handleMonthChange = (month) => {
        setFormData((prevData) => {
            if (month === "All Months") {
                // ✅ Select/Deselect all months
                return {
                    ...prevData,
                    bestMonthToVisit:
                        prevData.bestMonthToVisit.length === monthsList.length
                            ? [] // Deselect all if all were selected
                            : [...monthsList], // Select all
                };
            } else {
                const selectedMonths = prevData.bestMonthToVisit.includes(month)
                    ? prevData.bestMonthToVisit.filter((m) => m !== month)
                    : [...prevData.bestMonthToVisit, month];

                return { ...prevData, bestMonthToVisit: selectedMonths };
            }
        });
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        showToast("Submitting...", "info");
        const updatedFormData = { ...formData, category: segmentCategory };
        if (!updatedFormData.bannerImg || !updatedFormData.bannerImg.filePath || !updatedFormData.bannerImg.title) {
            delete updatedFormData.bannerImg;
        }
        try {
            await onSubmit(updatedFormData);
            showToast("Form submitted successfully!", "success");
        } catch (error) {
            console.error("❌ Error submitting form:", error);
            showToast("Failed to submit the form. Please try again.", "error");
        }
    };
    


    return (
        <form onSubmit={handleSubmit} id="overviewForm">
            <fieldset>
                <legend>
                    <h3>Overview</h3>
                </legend>
                <div className="formContainer">
                    <div className="row">
                        <div className="inputWrapper col-4 p-2">
                            <label htmlFor="locationName">Location Name</label>
                            <input
                                required
                                name="locationName"
                                value={formData.locationName}
                                onChange={handleChange}
                                type="text"
                                placeholder="Location Name..."
                            />
                        </div>
                        <div className="inputWrapper col-4 p-2">
                            <label htmlFor="category">Category</label>
                            <input
                                name="category"
                                value={formatSegmentLabel(segmentCategory)}
                                readOnly
                                type="text"
                                className="p-2"
                            />
                        </div>

                        <div className="inputWrapper col-4 p-2">
                            <label htmlFor="type">Type</label>
                            <input
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                type="text"
                                placeholder="Type"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="inputWrapper col-4 p-2">
                            <label htmlFor="description">Write Description:</label>
                            <textarea
                                name="description"
                                onChange={handleChange}
                                value={formData.description}
                                id="description"
                                placeholder="Write Description"
                            ></textarea>
                        </div>

                        <div className="inputWrapper col-4 p-2">
                            <ImageSelector
                                selectedImage={selectedImage}
                                handleImageSelection={setSelectedImage}
                                bannerImg={formData.bannerImg}
                                index={''}
                            />
                        </div>
                    </div>
                    {parentRange &&
                        <div className="row">
                            <div className="inputWrapper col-4 p-2">
                                <label htmlFor="parentRange">Parent Range</label>
                                <input
                                    name="parentRange"
                                    onChange={handleChange}
                                    value={formData.parentRange}
                                    id="parentRange"
                                    placeholder="Parent Range"
                                ></input>
                            </div>
                            <div className="inputWrapper col-4 p-2">
                                <label htmlFor="subRange">Sub Range</label>
                                <input
                                    name="subRange"
                                    onChange={handleChange}
                                    value={formData.subRange}
                                    id="subRange"
                                    placeholder="Sub Range"
                                ></input>
                            </div>
                            <div className="inputWrapper col-4 p-2">
                                <label htmlFor="Hashtags">Hashtags</label>
                                <input
                                    type="text"
                                    name="Hashtags"
                                    onChange={handleChange}
                                    value={formData.Hashtags}
                                    id="Hashtags"
                                    placeholder="#trek #shivajiMaharaja #fort #nashikTrek"
                                />
                            </div>
                            {/* Best month */}
                        </div>
                    }
                    <div className="row">
                        <div className="inputWrapper p-2">
                            <label htmlFor="address">Location Address</label>
                            <div id="address" style={{ display: "flex", gap: "15px" }}>
                                <input name="country" value={formData.address.country} onChange={handleChange} type="text" placeholder="Country" />
                                <input name="state" value={formData.address.state} onChange={handleChange} type="text" placeholder="State" />
                                <input name="district" value={formData.address.district} onChange={handleChange} type="text" placeholder="District" />
                                <input name="region" value={formData.address.region} onChange={handleChange} type="text" placeholder="Region" />
                                <input name="altitude" value={formData.address.altitude} onChange={handleChange} type="text" placeholder="Altitude" />
                            </div>

                        </div>

                    </div>
                    <hr />
                    <div className="row">
                        <div className="inputWrapper col-4 p-2">
                            <label htmlFor="monthlyVisitors">Monthly Visitors</label>
                            <input
                                name="monthlyVisitors"
                                type="text"
                                onChange={handleChange}
                                value={formData.monthlyVisitors}
                                placeholder="Monthly Visitors"
                            />
                        </div>
                        <div ref={dropdownRef} style={{ position: "relative", }} className="col-4 p-2">
                            <label style={{ fontWeight: "bold", marginBottom: "5px", display: "block" }}>
                                Best Months to Visit
                            </label>

                            {/* Dropdown Clickable Area */}
                            <div
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                style={{
                                    width: "fit-content",
                                    padding: "10px",
                                    border: "1px solid #ccc",
                                    borderRadius: "5px",
                                    background: "#f8f9fa",
                                    cursor: "pointer",
                                }}
                            >
                                {formData.bestMonthToVisit.length > 0
                                    ? formData.bestMonthToVisit.join(", ")
                                    : "Select months"}
                            </div>


                            {isDropdownOpen && (
                                <ul
                                    style={{
                                        position: "absolute",
                                        background: "white",
                                        border: "1px solid #ccc",
                                        width: "fit-content",
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                        zIndex: "100",
                                        padding: "0",
                                        margin: "5px 0",
                                        listStyle: "none",
                                    }}
                                >
                                    {/* ✅ "All Months" Option */}
                                    <li style={{ padding: "10px", cursor: "pointer" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.bestMonthToVisit.length === monthsList.length}
                                                onChange={() => handleMonthChange("All Months")}
                                                style={{ cursor: "pointer" }}
                                            />
                                            All Months
                                        </label>
                                    </li>

                                    {/* ✅ Individual Months */}
                                    {monthsList.map((month) => (
                                        <li key={month} style={{ padding: "10px", cursor: "pointer" }}>
                                            <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.bestMonthToVisit.includes(month)}
                                                    onChange={() => handleMonthChange(month)}
                                                    style={{ cursor: "pointer" }}
                                                />
                                                {month}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            )}

                        </div>
                    </div>


                    <div className="row">
                        <div className="inputWrapper p-2">
                            <button className="btn-blue" type="submit">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
    );
};

export { Overview };
